import type { PlanReviewChecklistResult, PlanReviewEvidence } from "@/api/plan_review";

export function humanizeSnakeCase(value: string): string {
  return value
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

export function shortChecklistStatusLabel(status: string): string {
  switch (status) {
    case "present":
      return "Met";
    case "missing":
      return "Missing";
    case "uncertain":
      return "Verify";
    default:
      return humanizeChecklistStatus(status);
  }
}

export function humanizeChecklistStatus(status: string): string {
  switch (status) {
    case "present":
      return "Meets requirement";
    case "missing":
      return "Missing or unclear";
    case "uncertain":
      return "Planner should verify";
    default:
      return humanizeSnakeCase(status);
  }
}

export function humanizeOrdinanceOutcome(outcome: string): string {
  switch (outcome) {
    case "compliant":
      return "Meets code";
    case "non_compliant":
      return "Does not meet code";
    case "needs_review":
      return "Planner should verify";
    default:
      return humanizeSnakeCase(outcome);
  }
}

export function statusPillClass(status: string) {
  switch (status) {
    case "present":
      return "bg-emerald-100 text-emerald-800";
    case "missing":
      return "bg-red-100 text-red-800";
    case "uncertain":
      return "bg-amber-100 text-amber-800";
    case "complete":
      return "bg-emerald-100 text-emerald-800";
    case "failed":
      return "bg-red-100 text-red-800";
    default:
      return "bg-slate-100 text-slate-800";
  }
}

export function completenessPillClass(pass: boolean) {
  return pass ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800";
}

export function completenessPillLabel(pass: boolean) {
  return pass ? "Pass" : "Needs review";
}

export function ordinanceOutcomePillClass(outcome: string) {
  switch (outcome) {
    case "compliant":
      return "bg-emerald-100 text-emerald-800";
    case "non_compliant":
      return "bg-red-100 text-red-800";
    case "needs_review":
      return "bg-amber-100 text-amber-800";
    default:
      return "bg-slate-100 text-slate-800";
  }
}

export function evidenceSourceLabel(sourceType?: string) {
  switch (sourceType) {
    case "text_block":
      return "OCR text";
    case "dimensional_fact":
      return "Measured dimension";
    case "structured_extract":
      return "Structured extract";
    case "vision":
      return "Vision review";
    case "page_chunk":
      return "Page context";
    default:
      return "Evidence";
  }
}

export function evidenceMatchedLabel(label?: string) {
  const value = label?.trim();
  if (!value) return null;

  const normalized = value.toLowerCase();
  if (["text", "text_block", "footer", "page_chunk", "page", "figure"].includes(normalized)) return null;

  return value
    .split(/[\s_]+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function provenanceLabel(item: PlanReviewChecklistResult) {
  const machineSource = item.machine_source || "";
  if (machineSource === "fused") return "text + vision";
  if (Array.isArray(item.evidence_sources) && item.evidence_sources.includes("vision")) return "vision";
  if (Array.isArray(item.evidence_sources) && item.evidence_sources.includes("structured_extract")) return "text + extract";
  if (Array.isArray(item.evidence_sources) && item.evidence_sources.includes("dimensional_fact")) return "text + dimensional facts";
  return "text-first";
}

export function formatCorrectionReason(reason?: string | null) {
  if (!reason) return null;
  return reason
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function firstEvidencePage(item: PlanReviewChecklistResult | null | undefined) {
  const page = item?.evidence?.find((evidence) => typeof evidence.page === "number")?.page;
  return typeof page === "number" ? page : null;
}

export function evidencePages(item: PlanReviewChecklistResult | null | undefined) {
  const pages = (item?.evidence || [])
    .map((evidence) => evidence.page)
    .filter((page): page is number => typeof page === "number");

  return [...new Set(pages)].sort((left, right) => left - right);
}

export function collapseDuplicateVisionEvidence(evidence: PlanReviewEvidence[]): PlanReviewEvidence[] {
  const collapsed: PlanReviewEvidence[] = [];
  const indexByKey = new Map<string, number>();

  for (const entry of evidence) {
    if (entry.source_type !== "vision") {
      collapsed.push(entry);
      continue;
    }

    const key = `${entry.page ?? ""}|${entry.excerpt ?? ""}`;
    const existingIndex = indexByKey.get(key);
    if (existingIndex == null) {
      indexByKey.set(key, collapsed.length);
      collapsed.push({ ...entry, bbox: Array.isArray(entry.bbox) ? [...entry.bbox] : entry.bbox });
      continue;
    }

    const existing = collapsed[existingIndex];
    if (!Array.isArray(entry.bbox) || entry.bbox.length !== 4) continue;
    existing.bbox =
      Array.isArray(existing.bbox) && existing.bbox.length === 4
        ? [
            Math.min(existing.bbox[0], entry.bbox[0]),
            Math.min(existing.bbox[1], entry.bbox[1]),
            Math.max(existing.bbox[2], entry.bbox[2]),
            Math.max(existing.bbox[3], entry.bbox[3]),
          ]
        : [...entry.bbox];
    existing.localized = true;
  }

  return collapsed;
}

export function hasLocalizedEvidence(item: PlanReviewChecklistResult | null | undefined) {
  return !!item?.evidence?.some(
    (evidence) => Array.isArray(evidence.bbox) && evidence.page_link_only !== true && evidence.localized !== false
  );
}

export function showsExtractedValue(item: PlanReviewChecklistResult | null | undefined) {
  if (!item || item.extracted_value == null || String(item.extracted_value).trim() === "") return false;
  return item.verification_mode !== "presence_only";
}

export function pageListLabel(pages: number[]) {
  if (pages.length === 0) return null;
  if (pages.length === 1) return `Page ${pages[0]}`;

  return `Pages ${pages.join(", ")}`;
}

export function formatElapsedTime(seconds: number) {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
}

export function humanizeRunStatus(status: string): string {
  switch (status) {
    case "pending":
      return "Pending";
    case "processing":
      return "Processing";
    case "complete":
      return "Complete";
    case "failed":
      return "Failed";
    default:
      return humanizeSnakeCase(status);
  }
}
